import React, { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import * as T from '../../../components/Table'
import {
  Box,
  Text,
  Tooltip,
  useToast,
  Icon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  List,
  ListItem,
  Flex,
  Badge,
  Divider,
} from '@chakra-ui/react'
import { ConfirmarValidacaoDeNf } from './ConfirmarValidacaoDeNf'
import { Validar } from './Validar'
import { RemoverNf } from './RemoverNf'
import { FiList, FiAlertCircle } from 'react-icons/fi'

const status = {
  0: 'red.500',
  1: 'green.500',
}

// Função para formatar a data usando date-fns
const formatarData = (dataString) => {
  try {
    if (!dataString) return ''

    // Verifica se a data já está no formato ISO ou precisa ser adaptada
    const data = dataString.includes('T')
      ? parseISO(dataString)
      : parseISO(dataString.replace(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/, '$1-$2-$3T$4:$5:$6'))

    return format(data, "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })
  } catch (error) {
    console.error('Erro ao formatar data:', error)
    return dataString // Retorna a string original em caso de erro
  }
}

// Componente para exibir o modal de erros
const ErrosValidacaoModal = ({ isOpen, onClose, erros }) => {
  if (!erros || erros.length === 0) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Validação de NF</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Não há erros de validação para esta NF.</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Erros de Validação da NF</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <List spacing={4}>
            {erros.map((erro, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Flex alignItems="center" mb={2}>
                      <Icon as={FiAlertCircle} color="red.500" mr={2} />
                      <Text fontWeight="bold">{erro.descricao_erro}</Text>
                    </Flex>
                    <Text mb={2}>{erro.mensagem_erro}</Text>
                    {erro.sugestao && (
                      <Flex alignItems="center" mt={2}>
                        <Text fontWeight="bold" mr={2}>
                          Sugestão:
                        </Text>
                        <Badge colorScheme="green" p={1}>
                          {erro.sugestao}
                        </Badge>
                      </Flex>
                    )}
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Data: {formatarData(erro.data_criacao)}
                    </Text>
                  </Box>
                </ListItem>
                {index < erros.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export const ValidacaoDeNfItem = ({ item, isUserPermitted }) => {
  const toast = useToast({ position: 'top-right' })
  const { isOpen, onOpen, onClose } = useDisclosure()

  function copyToClipboard(text) {
    toast.closeAll()

    navigator.clipboard.writeText(text)

    toast({
      title: 'Chave de acesso copaida com sucesso!',
      description: `A Chave ${text} foi copiada para a área de transferencia!`,
      duration: 5000,
    })
  }

  const temErros = item.validacao_nfe && item.validacao_nfe.length > 0

  return (
    <T.Tr>
      <T.Td>{item.dtpedido_compra}</T.Td>
      <T.Td>{item.dtemissao_nfe ? item.dtemissao_nfe : 'Pendente'}</T.Td>
      <T.Td>{item.data_entrada ? item.data_entrada : 'Pendente'}</T.Td>
      <T.Td>
        <Text as="a" href={item.link_pedido_compra} target="_blank" color="blue.500" _hover={{ textDecoration: 'underline' }}>
          {item.pedido}
        </Text>
      </T.Td>
      <T.Td>
        <Text>{item.numero_pedido}</Text>
      </T.Td>
      <T.Td>
        <Text>{item.fornecedor}</Text>
      </T.Td>
      <T.Td>
        <Text>{item.nfe}</Text>
      </T.Td>

      <T.Td>
        <Tooltip label={'Clique para copiar a chave de acesso!'}>
          <Text
            color="gray.700"
            fontWeight="semibold"
            cursor={'pointer'}
            textTransform="uppercase"
            fontSize="11px"
            onClick={() => copyToClipboard(item.chave_acesso)}
          >
            Chave de Acesso
          </Text>
        </Tooltip>
      </T.Td>
      <T.Td>
        <Text as="a" href={item.link_xml} color="blue.500" _hover={{ textDecoration: 'underline' }} target="_blank" rel="noreferrer">
          Link
        </Text>
      </T.Td>
      <T.Td>
        <Box px={2} textAlign="center" fontSize="sm">
          R$ {item.valor_nota}
        </Box>
      </T.Td>
      <T.Td>
        <Box px={2} textAlign="center" fontSize="sm">
          R$ {item.valor_pedido_compra || 'N/A'}
        </Box>
      </T.Td>
      <T.Td>
        <Box bg={status[item.nota_validada]} color="white" px={2} textAlign="center" fontSize="sm">
          {item.status}
        </Box>
      </T.Td>
      <T.Td>
        <Tooltip label={temErros ? 'Ver erros de validação' : 'Sem erros de validação'}>
          <Box
            cursor={temErros ? 'pointer' : 'default'}
            color={temErros ? 'red.500' : 'gray.300'}
            onClick={temErros ? onOpen : undefined}
            display="flex"
            justifyContent="center"
          >
            <Icon as={FiList} boxSize={5} />
            {temErros && (
              <Badge colorScheme="red" borderRadius="full" position="absolute" transform="translate(50%, -50%)" fontSize="xs">
                {item.validacao_nfe.length}
              </Badge>
            )}
          </Box>
        </Tooltip>
        <ErrosValidacaoModal isOpen={isOpen} onClose={onClose} erros={item.validacao_nfe} />
      </T.Td>
      <T.Td>{isUserPermitted && <ConfirmarValidacaoDeNf id={item.id} />}</T.Td>

      <T.Td>{isUserPermitted && <Validar {...item} />}</T.Td>

      <T.Td>{isUserPermitted && <RemoverNf id={item.id} />}</T.Td>
    </T.Tr>
  )
}
